import { failedTransferIcon, upcomingIcon } from "@assets";
import { ExclusiveLabel } from "@components/ExclusiveLabel";
import { Row } from "@components/Flex";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, BodySmall, Title4 } from "@components/Typography";
import { AccountStatus, TransactionStatus, TransactionType } from "@ctypes";
import { useAccount } from "@hooks";
import { i18n, Translations } from "@i18n";
import { convertCentsToDollar, formatCurrency } from "@utils";
import { icons } from "./content";
import { useStyles } from "./styles";
import { TransferItemProps } from "./types";

export const TransferItem = ({
  title,
  amountInCents,
  transactionType,
  status,
  beneficiaryName,
  goalName,
  upcoming = false,
  subscriptionPeriod,
  style,
  onClick,
}: TransferItemProps) => {
  const styles = useStyles();

  const { data } = useAccount();

  const hasErrorStatus = Boolean(
    status && [TransactionStatus.Failed, TransactionStatus.Cancelled].includes(status),
  );

  if (!title || amountInCents == null || !transactionType) return null;

  const subtitle = (() => {
    if (transactionType === TransactionType.SubscriptionFee && subscriptionPeriod) {
      return subscriptionPeriod;
    }

    return null;
  })();

  const icon = (() => {
    if (upcoming) return upcomingIcon;

    if (hasErrorStatus) {
      return failedTransferIcon;
    }

    return icons[transactionType];
  })();

  const accountInReview = Boolean(
    data?.find((a) =>
      [AccountStatus.New, AccountStatus.InReview, AccountStatus.Pending].includes(a.status!),
    ),
  );

  const formattedAmount = formatCurrency(convertCentsToDollar(amountInCents), true);

  return (
    <div style={{ ...styles.container, ...style }} onClick={onClick}>
      <Row>
        <img src={icon} alt="transfer" />
        <div style={styles.left}>
          <Body>{title}</Body>
          <Spacing size={SpacingSizes.XXS} />

          {subtitle && (
            <>
              <BodySmall style={styles.subtitle}>{subtitle}</BodySmall>
              <Spacing size={SpacingSizes.XXS} />
            </>
          )}

          {beneficiaryName || goalName ? (
            <>
              <Row>
                {beneficiaryName && (
                  <ExclusiveLabel style={styles.exclusive}>{beneficiaryName}</ExclusiveLabel>
                )}
                {goalName && <Title4 style={styles.goalLabel}>{goalName}</Title4>}
              </Row>
            </>
          ) : null}
        </div>
        <div style={styles.right}>
          <Body style={styles.amount}>{formattedAmount}</Body>
          {accountInReview && (
            <Body style={styles.secondary}>
              {i18n.t(Translations.TransfersRoutePendingAccountOpen)}
            </Body>
          )}
        </div>
      </Row>
    </div>
  );
};
